import React, { useState, useEffect } from 'react';
import { Building2, Calendar, Info, Search } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { supabase } from '@/integrations/supabase/client';
import { cn } from '@/lib/utils';

interface Organization {
  id: string;
  name: string;
}

interface AnalysisMetadataFormProps {
  analysisName: string;
  onNameChange: (name: string) => void;
  selectedOrganization: Organization | null;
  onOrganizationChange: (org: Organization | null) => void;
  detectedYear: number | undefined;
  selectedYear: number | undefined;
  onYearChange: (year: number | undefined) => void;
}

export const AnalysisMetadataForm: React.FC<AnalysisMetadataFormProps> = ({
  analysisName,
  onNameChange,
  selectedOrganization,
  onOrganizationChange,
  detectedYear,
  selectedYear,
  onYearChange,
}) => {
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [orgSearchOpen, setOrgSearchOpen] = useState(false);
  const [orgSearch, setOrgSearch] = useState('');

  // Fetch organizations
  useEffect(() => {
    const fetchOrganizations = async () => {
      const { data } = await supabase
        .from('organizations')
        .select('id, name')
        .is('deleted_at', null)
        .order('name');
      
      if (data) {
        setOrganizations(data);
      }
    };
    
    fetchOrganizations();
  }, []);

  // Generate year options (current year - 5 to current year + 1)
  const currentYear = new Date().getFullYear();
  const yearOptions = Array.from({ length: 7 }, (_, i) => currentYear - 5 + i);

  const filteredOrganizations = organizations.filter(org =>
    org.name.toLowerCase().includes(orgSearch.toLowerCase())
  );

  return (
    <Card>
      <CardHeader className="pb-4">
        <CardTitle className="text-base flex items-center gap-2">
          <Building2 className="h-4 w-4" />
          Analyse Koppelen
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Name */}
        <div className="space-y-2">
          <Label htmlFor="analysis-name">Naam</Label>
          <Input
            id="analysis-name"
            value={analysisName}
            onChange={(e) => onNameChange(e.target.value)}
            placeholder="Bijv. JWM Piekanalyse 2024"
          />
        </div>

        {/* Organization Combobox */}
        <div className="space-y-2">
          <Label>Organisatie (optioneel)</Label>
          <Popover open={orgSearchOpen} onOpenChange={setOrgSearchOpen}>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                role="combobox"
                aria-expanded={orgSearchOpen}
                className="w-full justify-between"
              >
                {selectedOrganization ? (
                  <span className="flex items-center gap-2">
                    <Building2 className="h-4 w-4" />
                    {selectedOrganization.name}
                  </span>
                ) : (
                  <span className="text-muted-foreground flex items-center gap-2">
                    <Search className="h-4 w-4" />
                    Zoek organisatie...
                  </span>
                )}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-[400px] p-0" align="start">
              <Command>
                <CommandInput
                  placeholder="Zoek organisatie..."
                  value={orgSearch}
                  onValueChange={setOrgSearch}
                />
                <CommandList>
                  <CommandEmpty>Geen organisaties gevonden</CommandEmpty>
                  <CommandGroup>
                    {selectedOrganization && (
                      <CommandItem
                        value="clear"
                        onSelect={() => {
                          onOrganizationChange(null);
                          setOrgSearchOpen(false);
                        }}
                        className="text-muted-foreground"
                      >
                        <span className="italic">Geen organisatie</span>
                      </CommandItem>
                    )}
                    {filteredOrganizations.map((org) => (
                      <CommandItem
                        key={org.id}
                        value={org.name}
                        onSelect={() => {
                          onOrganizationChange(org);
                          setOrgSearchOpen(false);
                        }}
                      >
                        <Building2 className={cn(
                          "mr-2 h-4 w-4",
                          selectedOrganization?.id === org.id ? "opacity-100" : "opacity-50"
                        )} />
                        {org.name}
                      </CommandItem>
                    ))}
                  </CommandGroup>
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
        </div>

        {/* Year */}
        <div className="space-y-2">
          <Label className="flex items-center gap-2">
            <Calendar className="h-4 w-4" />
            Analyse Jaar
            {detectedYear && (
              <Badge variant="outline" className="ml-2 text-xs">
                Gedetecteerd: {detectedYear}
              </Badge>
            )}
          </Label>
          <Select
            value={selectedYear?.toString() || detectedYear?.toString() || ''}
            onValueChange={(v) => onYearChange(v ? parseInt(v) : undefined)}
          >
            <SelectTrigger>
              <SelectValue placeholder="Selecteer jaar" />
            </SelectTrigger>
            <SelectContent>
              {yearOptions.map(year => (
                <SelectItem key={year} value={year.toString()}>
                  {year}
                  {year === detectedYear && ' (gedetecteerd)'}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Info */}
        {selectedOrganization && (
          <div className="flex items-start gap-2 p-3 bg-muted/50 rounded-lg text-sm">
            <Info className="h-4 w-4 text-muted-foreground mt-0.5 shrink-0" />
            <div className="text-muted-foreground">
              <p>Deze analyse wordt beschikbaar via:</p>
              <ul className="mt-1 space-y-0.5">
                <li>• Piekvermogen overzicht</li>
                <li>• Organisatie: <strong>{selectedOrganization.name}</strong></li>
              </ul>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default AnalysisMetadataForm;
